import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle } from 'lucide-react';
import { newsletterService } from '../services/api';

function parseJson(value, fallback) {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function Newsletter() {
  const [newsletter, setNewsletter] = useState({
    title: '',
    description: '',
    benefits: [],
  });
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchNewsletter = async () => {
      try {
        const response = await newsletterService.get();
        if (response.data) {
          setNewsletter({
            title: response.data.title || '',
            description: response.data.description || '',
            benefits: parseJson(response.data.benefits, []),
          });
        }
      } catch (error) {
        console.error('Error fetching newsletter:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNewsletter();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setEmail('');
      setSubmitted(false);
    }, 3000);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">خبرنامه</h1>
          <p className="text-xl text-blue-100">آخرین اخبار و انتشارات نشریه</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-12">
          <div className="text-center mb-8">
            <Mail className="w-16 h-16 text-blue-700 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{newsletter.title}</h2>
            <p className="text-lg text-gray-600">{newsletter.description}</p>
          </div>

          {submitted && (
            <div className="bg-green-50 border-r-4 border-green-600 text-green-800 p-6 rounded-lg mb-8 flex items-start gap-3">
              <CheckCircle size={24} className="flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold mb-1">با موفقیت اشتراک شدید</h3>
                <p>از این پس آخرین اخبار را در ایمیل خود دریافت خواهید کرد</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ایمیل خود را وارد کنید"
                required
                className="min-w-0 flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
              <button
                type="submit"
                className="bg-gradient-to-r from-blue-700 to-blue-900 text-white px-8 py-3 rounded-lg font-bold hover:from-blue-800 hover:to-blue-950 transition shadow-lg hover:shadow-xl"
              >
                اشتراک
              </button>
            </div>
          </form>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">مزایای اشتراک</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {newsletter.benefits.map((benefit, idx) => (
                <div key={idx} className="text-center">
                  <h4 className="font-bold text-gray-900 mb-2">{benefit.title}</h4>
                  <p className="text-gray-600 text-sm">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Newsletter;
